// Video utility functions for optimized loading and performance

export interface VideoLoadOptions {
  preload?: 'none' | 'metadata' | 'auto';
  quality?: string;
  timeout?: number;
}

export class VideoLoader {
  private static loadingVideos = new Set<string>();
  private static loadedVideos = new Set<string>();
  
  static async preloadVideo(src: string, options: VideoLoadOptions = {}): Promise<HTMLVideoElement> {
    const { preload = 'metadata', timeout = 10000 } = options;
    
    // Return immediately if already loaded
    if (this.loadedVideos.has(src)) {
      const video = document.createElement('video');
      video.src = src;
      return video;
    }
    
    // Prevent duplicate loading
    if (this.loadingVideos.has(src)) {
      return new Promise((resolve, reject) => {
        const checkLoaded = () => {
          if (this.loadedVideos.has(src)) {
            const video = document.createElement('video');
            video.src = src;
            resolve(video);
          } else if (!this.loadingVideos.has(src)) {
            reject(new Error('Video loading failed'));
          } else {
            setTimeout(checkLoaded, 100);
          }
        };
        checkLoaded();
      });
    }
    
    this.loadingVideos.add(src);
    
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = preload;
      video.muted = true;
      video.playsInline = true;
      
      const timeoutId = setTimeout(() => {
        cleanup();
        reject(new Error('Video loading timeout'));
      }, timeout);
      
      const cleanup = () => {
        clearTimeout(timeoutId);
        video.removeEventListener('canplay', handleCanPlay);
        video.removeEventListener('error', handleError);
        this.loadingVideos.delete(src);
      };
      
      const handleCanPlay = () => {
        cleanup();
        this.loadedVideos.add(src);
        resolve(video);
      };
      
      const handleError = () => {
        cleanup();
        reject(new Error('Video failed to load'));
      };
      
      video.addEventListener('canplay', handleCanPlay);
      video.addEventListener('error', handleError);
      
      video.src = src;
    });
  }
  
  static clearCache() {
    this.loadingVideos.clear();
    this.loadedVideos.clear();
  }
  
  static isLoaded(src: string): boolean {
    return this.loadedVideos.has(src);
  }
  
  static isLoading(src: string): boolean {
    return this.loadingVideos.has(src);
  }
}

export const formatVideoTime = (seconds: number): string => {
  if (isNaN(seconds) || seconds < 0) return "0:00";
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

export const getVideoQualityFromSrc = (src: string): string => {
  // Extract quality from filename if available
  const qualityMatch = src.match(/(\d+p)/);
  return qualityMatch ? qualityMatch[1] : '1080p';
};

export const generateVideoSources = (baseSrc: string, qualities: string[] = ['480p', '720p', '1080p']): string[] => {
  // In a real implementation, you would generate different quality URLs
  // For now, return the same source for all qualities
  return qualities.map(() => baseSrc);
};

// Intersection Observer for lazy loading videos
export class VideoIntersectionObserver {
  private static observer: IntersectionObserver | null = null;
  private static callbacks = new Map<Element, () => void>();
  
  static observe(element: Element, callback: () => void) {
    if (!this.observer) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const callback = this.callbacks.get(entry.target);
              if (callback) {
                callback();
                this.unobserve(entry.target);
              }
            }
          });
        },
        {
          rootMargin: '100px', // Start loading 100px before element comes into view
          threshold: 0.1
        }
      );
    }
    
    this.callbacks.set(element, callback);
    this.observer.observe(element);
  }
  
  static unobserve(element: Element) {
    if (this.observer) {
      this.observer.unobserve(element);
      this.callbacks.delete(element);
    }
  }
  
  static disconnect() {
    if (this.observer) {
      this.observer.disconnect();
      this.callbacks.clear();
      this.observer = null;
    }
  }
}